import {
  BOOKS_GLOBAL_STATUSES,
  BOOKS_GLOBAL_STATUS_COMPLETED,
  isManualBooksGlobalStatus,
} from "constants/booksGlobalStatus";
import { SUBMITTED } from "constants/taskStates";
import {
  BooksGlobalStatusUpdate,
  useBooksGlobalStatusUpdate,
} from "hooks/useBooksGlobalStatusUpdate";
import { useState } from "react";
import { useGetTaskQuery } from "store/apis/task";
import { BooksGlobalStatus } from "types/Models/bookkeepingTracker";
import { Task } from "types/Models/task";

export type BooksStatusOption = BooksGlobalStatus & {
  isCurrent: boolean;
  isDisabled: boolean;
};

export type BooksTaskStatus = {
  isBooksTask: boolean;
  isCompleted: boolean;
  isLoading: boolean;
  options: BooksStatusOption[];
  currentOption: BooksStatusOption | undefined;
  onSelect: (key?: string | null) => void;
  statusUpdate: BooksGlobalStatusUpdate;
  fullTask: Task | undefined;
  completedStateId: string | undefined;
  isCompletePending: boolean;
  closeComplete: () => void;
};

// Option building and selection handling for the Books status dropdowns on the task page
export const useBooksTaskStatus = ({
  task,
}: {
  task: Task;
}): BooksTaskStatus => {
  const currentStatus = task.books_global_status ?? null;
  const isBooksTask = Boolean(currentStatus);
  const statusUpdate = useBooksGlobalStatusUpdate(task.uuid);
  const [isCompletePending, setIsCompletePending] = useState(false);

  // Chat header tasks don't carry workflow states, so load the full task then
  const { data: fetchedTask } = useGetTaskQuery(
    { taskId: task.uuid },
    { skip: !isBooksTask || Boolean(task.task_states) }
  );
  const fullTask = task.task_states ? task : fetchedTask;
  const completedStateId = fullTask?.task_states?.find(
    (state) => state.type_str === SUBMITTED
  )?.uuid;

  // Completed is selectable here but goes through the task completion flow
  const options: BooksStatusOption[] = BOOKS_GLOBAL_STATUSES.map((status) => ({
    ...status,
    isCurrent: status.key === currentStatus?.key,
    isDisabled:
      status.key === BOOKS_GLOBAL_STATUS_COMPLETED
        ? !completedStateId
        : !isManualBooksGlobalStatus(status.key),
  }));

  const onSelect = (key?: string | null) => {
    const selected = options.find((option) => option.key === key);
    if (!selected || selected.isDisabled || selected.isCurrent) return;
    if (selected.key === BOOKS_GLOBAL_STATUS_COMPLETED) {
      setIsCompletePending(true);
      return;
    }
    statusUpdate.requestStatusChange(selected);
  };

  return {
    isBooksTask,
    isCompleted: currentStatus?.key === BOOKS_GLOBAL_STATUS_COMPLETED,
    isLoading: statusUpdate.isUpdating,
    options,
    currentOption: options.find((option) => option.isCurrent),
    onSelect,
    statusUpdate,
    fullTask,
    completedStateId,
    isCompletePending,
    closeComplete: () => setIsCompletePending(false),
  };
};
